import { Alert, Box, Button, Stack } from '@mui/material'
import { useTranslate } from '@tolgee/react'
import { ConfirmationDialog } from '@tom-ui/ui'
import { appStore } from '@tom-ui/utils'
import { PositionDto, YardBlockDto } from '@yard/app/api'
import { BlockMiniMap } from '@yard/components/MiniMap/BlockMiniMap'
import { housekeepingPlanService } from '@yard/services/housekeeping-plan.service'
import { OccupancyStore } from '@yard/stores/OccupancyStore'
import { YardBlockStore } from '@yard/stores/YardBlockStore'
import { observer } from 'mobx-react-lite'
import { useEffect, useMemo, useState } from 'react'
import { ICreateHousekeepingPlanFormData } from '../../../models/yard-setup.model'
import { CreateHousekeepingPlanFormStore } from '../../../stores/create-housekeeping-plan.form-store'
import { HousekeepingPlanAccumulatorViewStore } from '../../../stores/housekeeping-plan-accumulator.view-store'
import { capacityState } from './capacity-state'
import { mapFormToCreatePlanRequest } from './create-housekeeping-plan.mapper'
import { CreateHousekeepingPlanForm } from './CreateHousekeepingPlanForm'

const CREATE_HOUSEKEEPING_PLAN_FORM_ID = 'create-housekeeping-plan-form'

interface ApiErrorShape {
  response?: { status?: number; data?: { detail?: string } }
}

interface Props {
  housekeepingPlanAccumulatorViewStore: HousekeepingPlanAccumulatorViewStore
  yardBlockStore: YardBlockStore
  occupancyStore: OccupancyStore
}

export const CreateHousekeepingPlanDialog = observer(
  ({ housekeepingPlanAccumulatorViewStore, yardBlockStore, occupancyStore }: Props) => {
    const { t } = useTranslate()
    // Lifted up from Content so we can disable the ConfirmationDialog's
    // primary action when the form isn't actually submittable. The signal
    // itself is derived inside Content (where the relevant state lives)
    // and pushed up via onCanSubmitChange.
    const [canSubmit, setCanSubmit] = useState(false)

    return (
      <ConfirmationDialog
        open={housekeepingPlanAccumulatorViewStore.isPlanDialogOpen}
        title={t('createHousekeepingPlan', 'Create housekeeping plan')}
        primaryActionText={t('yesPlan', 'Yes, plan')}
        primaryActionDisabled={!canSubmit}
        closeLabel={t('noCancel', 'No, cancel')}
        formId={CREATE_HOUSEKEEPING_PLAN_FORM_ID}
        // Lock the dialog width — ConfirmationDialog only enables MUI's
        // fullWidth when a maxWidth prop is passed, so without this the
        // dialog reflows to its intrinsic content width as the alert copy
        // grows and shrinks. md keeps it stable across all three states.
        maxWidth='md'
        onClose={() => housekeepingPlanAccumulatorViewStore.closePlanDialog()}
      >
        <CreateHousekeepingPlanDialogContent
          housekeepingPlanAccumulatorViewStore={housekeepingPlanAccumulatorViewStore}
          yardBlockStore={yardBlockStore}
          occupancyStore={occupancyStore}
          onCanSubmitChange={setCanSubmit}
        />
      </ConfirmationDialog>
    )
  },
)

interface ContentProps extends Props {
  onCanSubmitChange: (canSubmit: boolean) => void
}

const CreateHousekeepingPlanDialogContent = observer(
  ({
    housekeepingPlanAccumulatorViewStore,
    yardBlockStore,
    occupancyStore,
    onCanSubmitChange,
  }: ContentProps) => {
    const { t } = useTranslate()
    const formStore = useMemo(() => new CreateHousekeepingPlanFormStore(), [])
    const [selectedPositions, setSelectedPositions] = useState<PositionDto[]>([])

    // Selections belong to a specific block — clear them when the user picks a different block.
    useEffect(() => {
      setSelectedPositions([])
    }, [formStore.blockId.data])

    const checkedContainerIds = useMemo(
      () => [...housekeepingPlanAccumulatorViewStore.checked],
      [housekeepingPlanAccumulatorViewStore.checked],
    )

    // Drop reports from the last dispatch attempt. Split per reason so each
    // gets its own inline alert / snackbar — planners react differently to
    // "moved since accumulation" vs "already in another plan".
    const [raceMovedContainerIds, setRaceMovedContainerIds] = useState<number[]>([])
    const [alreadyInPlanContainerIds, setAlreadyInPlanContainerIds] = useState<number[]>([])

    const selectedBlock = useMemo<YardBlockDto | undefined>(
      () => yardBlockStore.items.find(b => b.id === formStore.blockId.data),
      [yardBlockStore.items, formStore.blockId.data],
    )

    const occupiedPositions = useMemo<PositionDto[]>(() => {
      if (!selectedBlock) return []
      const occupancies = occupancyStore.occupanciesByBlock[selectedBlock.id] ?? []
      return occupancies.map(o => o.position).filter((p): p is PositionDto => !!p)
    }, [selectedBlock, occupancyStore.occupanciesByBlock])

    const containerCount = checkedContainerIds.length

    // Each click on a free tier in BlockMiniMap appends one PositionDto, and
    // the minimap refuses clicks past a cell's free capacity. So the number
    // of destination slots the user has actually committed to == the length
    // of `selectedPositions`. No BE round-trip needed — `createPlan` remains
    // the authoritative gate.
    const freeSlots = selectedPositions.length
    const { severity, fitsAll, deficit } = capacityState(freeSlots, containerCount)
    const isEmpty = selectedPositions.length === 0

    // Only show the "Dispatch what fits" toggle when there's an actual deficit
    // for the user to opt out of. Pre-selection (no positions picked yet)
    // would render the toggle as noise and would let the user pre-toggle it
    // before knowing whether they'd even hit the deficit case. The form-store
    // guard in CreateHousekeepingPlanForm clears the value if deficit ever flips
    // back to false, so hidden + stale state can't drift.
    const hasDeficit = selectedPositions.length > 0 && !fitsAll

    useEffect(() => {
      onCanSubmitChange(
        formStore.blockId.data !== '' &&
          selectedPositions.length > 0 &&
          containerCount > 0 &&
          (fitsAll || (formStore.dispatchWhatFits.data && freeSlots > 0)),
      )
    }, [
      formStore.blockId.data,
      formStore.dispatchWhatFits.data,
      selectedPositions.length,
      containerCount,
      fitsAll,
      freeSlots,
      onCanSubmitChange,
    ])

    const handleSubmit = async (formData: ICreateHousekeepingPlanFormData) => {
      if (selectedPositions.length === 0) {
        appStore.setShowAlert(
          'warning',
          t('selectAtLeastOneCell', 'Select at least one destination cell.'),
        )
        return
      }
      try {
        const containerPositions = housekeepingPlanAccumulatorViewStore.snapshotsForChecked()
        const request = mapFormToCreatePlanRequest(formData, containerPositions, selectedPositions)
        const result = await housekeepingPlanService.createPlan(request)
        const raceMoved = result.raceMovedContainerIds ?? []
        const alreadyInPlan = result.alreadyInPlanContainerIds ?? []
        setRaceMovedContainerIds(raceMoved)
        setAlreadyInPlanContainerIds(alreadyInPlan)

        // Resolve internal ids to ISO box codes (BMOU1234567). Planners
        // recognise the box code; the internal id is meaningless to them.
        const formatIds = (ids: number[]) =>
          ids
            .map(id => housekeepingPlanAccumulatorViewStore.containerNumberFor(id) ?? `#${id}`)
            .join(', ')

        if (result.plan == null) {
          // Every accumulated container was dropped (race-moved or already in
          // another plan). Per acceptance criterion #4 the dialog stays open
          // and shows the inline warnings so the planner can react.
          appStore.setShowAlert(
            'warning',
            t(
              'planAllDropped',
              'No plan was created — every container in the group was either moved or is already in another plan.',
            ),
          )
          return
        }

        // Partial-drop path: at least one container went through. Show a green
        // success snackbar plus up to two warning snackbars (one per drop
        // reason) so the planner can't miss the silent removals once the
        // modal closes.
        appStore.setShowAlert('success', t('planCreated', 'Housekeeping plan created.'))
        if (raceMoved.length > 0) {
          appStore.setShowAlert(
            'warning',
            t(
              'planRaceMovedContainers',
              '{count} container(s) were dropped because they have been moved: {ids}',
              { count: raceMoved.length, ids: formatIds(raceMoved) },
            ),
          )
        }
        if (alreadyInPlan.length > 0) {
          appStore.setShowAlert(
            'warning',
            t(
              'planAlreadyInPlanContainers',
              '{count} container(s) were dropped because they are already in another plan: {ids}',
              { count: alreadyInPlan.length, ids: formatIds(alreadyInPlan) },
            ),
          )
        }
        housekeepingPlanAccumulatorViewStore.clearAccumulator()
        housekeepingPlanAccumulatorViewStore.closePlanDialog()
      } catch (error) {
        const detail = (error as ApiErrorShape)?.response?.data?.detail
        appStore.setShowAlert('error', detail ?? t('planCreateFailed', 'Could not create plan.'))
      }
    }

    // Overselection signal: the user picked more destination slots than the
    // group has containers. The BE stores all selected slots on the plan but
    // only dispatches containerCount moves; equipment operators pick which
    // slot to land each container in at execution time. Warn the user that
    // the surplus picks aren't a 1:1 reservation.
    const surplusSlots = Math.max(selectedPositions.length - containerCount, 0)

    return (
      <Stack spacing={2}>
        {/* Reserve a footprint that covers the longest copy (deficit, wraps
            to ~2 lines at md width) so the dialog body doesn't shrink as
            the alert flips between empty / deficit / fits states. */}
        <Alert severity={isEmpty ? 'info' : severity} sx={{ minHeight: 88 }}>
          {isEmpty
            ? t('capacityEmpty', 'Select destination slots for {containers} container(s).', {
                containers: containerCount,
              })
            : fitsAll
              ? t('capacityFits', 'Selected {free} slot(s) for {containers} container(s).', {
                  free: freeSlots,
                  containers: containerCount,
                })
              : t(
                  'capacityDeficit',
                  'You have only selected {free} of {containers} required slot(s). Add {deficit} more destination slot(s), or enable "Dispatch what fits" below to dispatch only the containers that fit.',
                  { free: freeSlots, containers: containerCount, deficit },
                )}
        </Alert>

        {surplusSlots > 0 && (
          <Alert severity='warning'>
            {t(
              'capacitySurplus',
              'You selected {selected} destination slot(s) for {containers} container(s). Only {containers} will be used — equipment operators pick which slot at execution time.',
              { selected: selectedPositions.length, containers: containerCount },
            )}
          </Alert>
        )}

        {/* Drop warnings — one inline alert per reason. We translate the
            ids back to ISO container numbers via the accumulator snapshot
            map (planners recognise "BMOU1234567", not the internal id) and
            fall back to "#<id>" when the master-data hadn't loaded yet at
            accumulation time. Alerts only render when their list is non-
            empty so a clean dispatch shows nothing here. */}
        {raceMovedContainerIds.length > 0 && (
          <Alert severity='warning' data-testid='race-moved-alert'>
            {t(
              'planRaceMovedContainers',
              '{count} container(s) were dropped because they have been moved: {ids}',
              {
                count: raceMovedContainerIds.length,
                ids: raceMovedContainerIds
                  .map(
                    (id: number) =>
                      housekeepingPlanAccumulatorViewStore.containerNumberFor(id) ?? `#${id}`,
                  )
                  .join(', '),
              },
            )}
          </Alert>
        )}
        {alreadyInPlanContainerIds.length > 0 && (
          <Alert severity='warning' data-testid='already-in-plan-alert'>
            {t(
              'planAlreadyInPlanContainers',
              '{count} container(s) were dropped because they are already in another plan: {ids}',
              {
                count: alreadyInPlanContainerIds.length,
                ids: alreadyInPlanContainerIds
                  .map(
                    (id: number) =>
                      housekeepingPlanAccumulatorViewStore.containerNumberFor(id) ?? `#${id}`,
                  )
                  .join(', '),
              },
            )}
          </Alert>
        )}

        <CreateHousekeepingPlanForm
          id={CREATE_HOUSEKEEPING_PLAN_FORM_ID}
          formStore={formStore}
          blocks={yardBlockStore.items}
          hasDeficit={hasDeficit}
          onSubmit={handleSubmit}
        />

        {selectedBlock && (
          <Box>
            <BlockMiniMap
              block={selectedBlock}
              occupiedPositions={occupiedPositions}
              selectedPositions={selectedPositions}
              onSelectionChange={setSelectedPositions}
            />
            <Stack direction='row' justifyContent='flex-end' mt={1}>
              <Button
                size='small'
                variant='text'
                color='inherit'
                disabled={selectedPositions.length === 0}
                onClick={() => setSelectedPositions([])}
              >
                {t('clearDestinations', 'Clear destinations')}
              </Button>
            </Stack>
          </Box>
        )}
      </Stack>
    )
  },
)
